import { useCallback, useMemo } from 'react';
import {
  CalendarDaysIcon,
  ClipboardCheckThinIcon,
  DownloadIcon,
  GearIcon,
  HouseIcon,
  PeopleIcon,
} from '@ghq-abi/design-system-icons';
import { useTranslate } from '@tolgee/react';
import { DashboardScope } from 'abi-lcm-common-types';

import { useAbility } from '~/app/contexts/AbilityContext';
import { useFlags } from '~/app/contexts/FlagsContext';
import { RouteObject } from '~/app/types';
import { ResultsIcon } from '~/shared/components/icons';
import { IdpIcon } from '~/shared/components/icons/IdpIcon';
import { pagesPaths, PagesPathsKeys } from '~/shared/constants/pages';

export function useMenuRoutes() {
  const { t } = useTranslate();
  const ability = useAbility();
  const flags = useFlags();

  const hasAuthorizedFlags = useCallback(
    (authorizedFlags: PagesPathsKeys | PagesPathsKeys[]) => {
      if (
        typeof authorizedFlags === 'string' &&
        !flags[authorizedFlags].active
      ) {
        return false;
      }
      if (
        Array.isArray(authorizedFlags) &&
        !authorizedFlags.some(flag => flags[flag].active)
      ) {
        return false;
      }
      return true;
    },
    [flags]
  );

  const getDashboardPathByScope = useCallback(
    (scope: DashboardScope) => {
      const dashboardRoutes: RouteObject[] = [];

      const scopeAllowed =
        (scope === 'myself' && ability.can('view', 'DashboardAsAdmin')) ||
        (scope === 'my-team' && ability.can('view', 'DashboardAsManager'));

      if (!scopeAllowed) return dashboardRoutes;

      if (
        hasAuthorizedFlags('nominationsDashboard') &&
        ability.can('view', 'NominationsDashboardMenu')
      ) {
        dashboardRoutes.push({
          key: 'nominationsDashboard',
          path: `/nominations-dashboard?scope=${scope}`,
          title: t('menu.nominations_dashboard'),
        });
      }

      if (
        hasAuthorizedFlags('assessmentsDashboard') &&
        ability.can('view', 'AssessmentsDashboardMenu')
      ) {
        dashboardRoutes.push({
          key: 'assessmentsDashboard',
          path: `/assessments-dashboard?scope=${scope}`,
          title: t('menu.assessments_dashboard'),
        });
      }

      return dashboardRoutes;
    },
    [ability, hasAuthorizedFlags, t]
  );

  const { menuRoutes, footerRoutes } = useMemo(() => {
    const menuRoutesList: RouteObject[] = [];
    const footerRoutesList: RouteObject[] = [];

    // Common routes for all profiles
    if (hasAuthorizedFlags('home') && ability.can('view', 'Home')) {
      menuRoutesList.push({
        key: 'home',
        icon: <HouseIcon />,
        path: '/home',
        title: t('menu.home'),
      });
    }

    if (
      hasAuthorizedFlags('nominations') &&
      ability.can('view', 'Nominations')
    ) {
      menuRoutesList.push({
        key: 'nominations',
        icon: <PeopleIcon />,
        path: '/nominations',
        title: t('menu.nominations'),
      });
    }

    if (
      hasAuthorizedFlags('evaluations') &&
      ability.can('view', 'Evaluations')
    ) {
      menuRoutesList.push({
        key: 'evaluations',
        icon: <ClipboardCheckThinIcon />,
        path: '/evaluations',
        title: t('menu.assessments'),
      });
    }

    if (hasAuthorizedFlags('results') && ability.can('view', 'SelfResults')) {
      menuRoutesList.push({
        key: 'results',
        icon: <ResultsIcon />,
        path: pagesPaths.results,
        title: t('menu.my_results'),
      });
    }

    if (!hasAuthorizedFlags('results') && ability.can('view', 'Results')) {
      menuRoutesList.push({
        key: 'results',
        icon: <CalendarDaysIcon />,
        path: '/reports',
        title: t('menu.my_results'),
      });
    }

    if (hasAuthorizedFlags('idp') && ability.can('view', 'Idp')) {
      menuRoutesList.push({
        key: 'idp',
        icon: <IdpIcon />,
        path: '/idp/individual',
        title: t('menu.my_idp'),
      });
    }

    if (
      hasAuthorizedFlags('downloadDocuments') &&
      ability.can('view', 'DownloadDocuments')
    ) {
      menuRoutesList.push({
        key: 'downloadDocuments',
        icon: <DownloadIcon />,
        path: '/download-documents',
        title: t('menu.my_downloads'),
      });
    }

    // Manager and Admin specific routes
    const teamRoutes: RouteObject[] = [];

    if (
      hasAuthorizedFlags('managerApproval') &&
      ability.can('view', 'ManagerApproval')
    ) {
      teamRoutes.push({
        key: 'managerApproval',
        path: '/manager-approval',
        title: t('menu.manager_approval'),
      });
    }

    teamRoutes.push(...getDashboardPathByScope('my-team'));

    if (ability.can('view', 'TeamReports')) {
      teamRoutes.push({
        key: 'teamReports',
        path: '/reports-manager',
        title: t('menu.team_results'),
      });
    }

    if (hasAuthorizedFlags('idp') && ability.can('view', 'TeamIdp')) {
      teamRoutes.push({
        key: 'teamIdp',
        path: '/idp/my-team',
        title: t('menu.team_idp'),
      });
    }

    if (teamRoutes.length > 0) {
      menuRoutesList.push({
        key: 'myTeam',
        icon: <PeopleIcon />,
        title: t('menu.my_team'),
        path: '/my-team',
        routeComponents: teamRoutes,
      });
    }

    // Admin specific routes
    const adminRoutes: RouteObject[] = [];

    if (hasAuthorizedFlags('admin') && ability.can('view', 'Admin')) {
      adminRoutes.push({
        key: 'manageParticipants',
        path: '/admin',
        title: t('menu.manage_participants'),
      });

      adminRoutes.push(...getDashboardPathByScope('myself'));

      if (ability.can('view', 'Results')) {
        adminRoutes.push({
          key: 'adminReports',
          path: '/reports',
          title: t('menu.results'),
        });
      }
    }

    if (
      hasAuthorizedFlags(['roleSetting']) &&
      ability.can('manage', 'RoleSetting')
    ) {
      adminRoutes.push({
        key: 'roleSetting',
        path: '/role-setting',
        title: t('menu.role_settings'),
      });
    }

    if (adminRoutes.length > 0) {
      menuRoutesList.push({
        key: 'admin',
        icon: <GearIcon />,
        title: t('menu.admin'),
        path: '/admin',
        routeComponents: adminRoutes,
      });
    }

    return { menuRoutes: menuRoutesList, footerRoutes: footerRoutesList };
  }, [ability, getDashboardPathByScope, hasAuthorizedFlags, t]);

  return { menuRoutes, footerRoutes };
}
